import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Leaf, User, Mail, Crown, Calendar, TreePine, Camera, BookOpen,
  ArrowLeft, LogOut, Sparkles, Store, Plus, Wallet, ShoppingBag
} from "lucide-react";

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const getTierInfo = () => {
    switch (user?.subscription_tier) {
      case "premium":
        return { name: "Master Gardener", color: "bg-purple-100 text-purple-700", icon: <Crown className="w-4 h-4" /> };
      case "standard":
        return { name: "The Gardener", color: "bg-secondary/20 text-secondary", icon: <Sparkles className="w-4 h-4" /> };
      default:
        return { name: "The Planner", color: "bg-muted text-muted-foreground", icon: <Leaf className="w-4 h-4" /> };
    }
  };

  const tierInfo = getTierInfo();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/saved" className="text-muted-foreground hover:text-primary">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-serif text-xl font-bold">Profile</h1>
          </div>
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="RootSpace.Pro" className="h-8 w-auto" />
            
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Profile Card */}
        <Card className="card-organic mb-8">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <Avatar className="w-24 h-24">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="text-2xl bg-primary text-primary-foreground">
                  {user?.name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              
              <div className="text-center md:text-left flex-1">
                <h2 className="font-serif text-2xl font-bold mb-1">{user?.name}</h2>
                <p className="text-muted-foreground flex items-center justify-center md:justify-start gap-2">
                  <Mail className="w-4 h-4" />
                  {user?.email}
                </p>
                <Badge className={`mt-3 ${tierInfo.color}`}>
                  {tierInfo.icon}
                  <span className="ml-1">{tierInfo.name}</span>
                </Badge>
              </div>

              <div className="flex flex-col gap-2">
                {user?.subscription_tier === "free" && (
                  <Button 
                    onClick={() => navigate('/pricing')}
                    className="bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Upgrade
                  </Button>
                )}
                <Button variant="outline" onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="card-organic">
            <CardContent className="p-4 text-center">
              <TreePine className="w-8 h-8 mx-auto mb-2 text-primary" />
              <p className="font-mono text-2xl font-bold">{user?.plan_count || 0}</p>
              <p className="text-sm text-muted-foreground">Garden Plans</p>
            </CardContent>
          </Card>
          <Card className="card-organic">
            <CardContent className="p-4 text-center">
              <Camera className="w-8 h-8 mx-auto mb-2 text-secondary" />
              <p className="font-mono text-2xl font-bold">{user?.plant_identifications_this_month || 0}</p>
              <p className="text-sm text-muted-foreground">Plants Identified</p>
            </CardContent>
          </Card>
          <Card className="card-organic">
            <CardContent className="p-4 text-center">
              <BookOpen className="w-8 h-8 mx-auto mb-2 text-primary" />
              <p className="font-mono text-2xl font-bold">{user?.pdf_exports_count || 0}</p>
              <p className="text-sm text-muted-foreground">PDF Exports</p>
            </CardContent>
          </Card>
          <Card className="card-organic">
            <CardContent className="p-4 text-center">
              <Calendar className="w-8 h-8 mx-auto mb-2 text-secondary" />
              <p className="font-mono text-sm font-bold">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
              </p>
              <p className="text-sm text-muted-foreground">Member Since</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="card-organic">
          <CardHeader>
            <CardTitle className="font-serif text-xl">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-4">
              <Button 
                variant="outline" 
                className="h-auto py-4 flex-col"
                onClick={() => navigate('/create')}
              >
                <TreePine className="w-6 h-6 mb-2" />
                Create New Plan
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-4 flex-col"
                onClick={() => navigate('/identify')}
              >
                <Camera className="w-6 h-6 mb-2" />
                Identify Plant
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-4 flex-col"
                onClick={() => navigate('/saved')}
              >
                <Leaf className="w-6 h-6 mb-2" />
                My Gardens
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-4 flex-col border-primary/50 hover:bg-primary/10"
                onClick={() => navigate('/marketplace/sell')}
              >
                <Store className="w-6 h-6 mb-2 text-primary" />
                Sell Seeds
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Marketplace Section */}
        <Card className="card-organic mt-6 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
          <CardHeader>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <Store className="w-5 h-5 text-amber-600" />
              Seed Marketplace
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              Share your seeds with the community! List your heirloom and organic seeds for sale.
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <Button 
                onClick={() => navigate('/marketplace/sell')}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                List Seeds for Sale
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/marketplace/wallet')}
                className="border-amber-300"
              >
                <Wallet className="w-4 h-4 mr-2" />
                Seller Wallet
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/marketplace')}
                className="border-amber-300"
              >
                <ShoppingBag className="w-4 h-4 mr-2" />
                Browse Seeds
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ProfilePage;
